import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DwEventService, DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { BehaviorSubject, combineLatest, concat, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, map, shareReplay, startWith, switchMap, take, timeout } from 'rxjs/operators';
import { AppMetaData, AppMetaDataItemNames, RoChallengeAdminSummaryEntity,
    RoChallengeCollectionSummaryEntity, RoChallengeStreakerAdminEntity } from '../../../meta-data/app-meta-data.service';
import { ChallengeContextService } from '../../shared/contexts/challenge-context/challenge-context.service';
import { CentralAdminAccessService } from './central-admin-access.service';

export interface CentralAdminState {
    loading: boolean;
    error: string;
    summary: RoChallengeAdminSummaryEntity;
    collection: RoChallengeCollectionSummaryEntity;
    milestones: RoChallengeStreakerAdminEntity[];
}

/** Component-scoped snapshots. Account/challenge changes cancel in-flight reads. */
@Injectable()
export class CentralAdminDataService implements OnDestroy {
    readonly items = AppMetaDataItemNames;
    readonly state$: Observable<CentralAdminState>;
    private readonly _refresh = new BehaviorSubject<void>(undefined);
    private readonly _subscriptions = new Subscription();

    constructor(@Inject(DwOrmDataServiceToken) private _orm: DwOrmDataService,
        private _access: CentralAdminAccessService, selection: ChallengeContextService,
        security: DwSecurityUserService, events: DwEventService) {
        const empty: CentralAdminState = { loading: false, error: '', summary: null, collection: null, milestones: [] };
        this.state$ = combineLatest([selection.challengeChange$,
            security.securityContextChanged().pipe(startWith(null)), this._refresh]).pipe(
            debounceTime(0), switchMap(([selected]) => {
                if (!this._access.allowed) { return of(empty); }
                if (!(selected.ChallengeDefinitionId > 0)) { return of({ ...empty, error: 'Choose a streak to view its reports.' }); }
                const id = selected.ChallengeDefinitionId;
                return concat(of({ ...empty, loading: true }), combineLatest([
                    this.loadSummary(id), this.loadCollection(id), this.loadMilestones(id),
                ]).pipe(take(1), timeout(20000), map(([summary, collection, milestones]) => {
                    if (summary.length !== 1 || collection.length !== 1) { throw new Error('Missing or duplicate challenge summary.'); }
                    return { ...empty, summary: summary[0], collection: collection[0], milestones };
                }), catchError(() => of({ ...empty, error: 'Unable to load streak totals. Please try again.' }))));
            }), shareReplay({ bufferSize: 1, refCount: true }));
        [AppMetaData.UserChallenge, AppMetaData.DwOrder, AppMetaData.DwOrderDetail,
            AppMetaData.RegistrationOrderDetail, AppMetaData.ChallengeDefinition].forEach(item => {
            this._subscriptions.add(events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item.ItemDetail.itemName))
                .subscribe(() => this.refresh()));
        });
    }

    refresh(): void { this._refresh.next(); }

    private loadSummary(challengeId: number): Observable<RoChallengeAdminSummaryEntity[]> {
        if (!this._access.allowed) { return throwError(new Error('Administrator access required.')); }
        const query = AppMetaData.RoChallengeAdminSummary.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId));
        return this._orm.executeQuery<RoChallengeAdminSummaryEntity>(query);
    }

    private loadCollection(challengeId: number): Observable<RoChallengeCollectionSummaryEntity[]> {
        if (!this._access.allowed) { return throwError(new Error('Administrator access required.')); }
        const query = AppMetaData.RoChallengeCollectionSummary.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId));
        return this._orm.executeQuery<RoChallengeCollectionSummaryEntity>(query);
    }

    private loadMilestones(challengeId: number): Observable<RoChallengeStreakerAdminEntity[]> {
        if (!this._access.allowed) { return throwError(new Error('Administrator access required.')); }
        const query = AppMetaData.RoChallengeStreakerAdmin.CreateQueryBuilder()
            .addFields(a => [a.RowNumber, a.DisplayName, a.RegistrationDateTime])
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsMilestone, true))
            .addOrderByDesc(a => a.RowNumber).setupPaging(1, 5, false, 5);
        return this._orm.executeQuery<RoChallengeStreakerAdminEntity>(query);
    }

    ngOnDestroy(): void { this._subscriptions.unsubscribe(); this._refresh.complete(); }
}
